import type { PastorFile, PastorProfile } from '../pastorApi';
import { fileSizeText, youtubeThumb } from '../pastorApi';
import { RURAL_LABEL } from '../pastorOptions';

// 담임목회 희망 이력서 전체 — 교회가 전달받은 이력서 화면 · 사역자 본인 미리보기
// onOpenFile 을 주면 구직 서류(자기소개서 등)를 내려받을 수 있게 보여 준다
export default function PastorProfileView({ profile, onOpenFile }: { profile: PastorProfile; onOpenFile?: (f: PastorFile) => void }) {
  const p = profile;
  const thisYear = new Date().getFullYear();
  const rows: [string, string][] = [
    ['연락처', [p.phone, p.email].filter(Boolean).join(' · ')],
    ['연배', p.ageBand || (p.birthYear ? `${p.birthYear}년생` : '')],
    ['교단', p.denomination],
    ['사역 경력', p.ministryYears != null ? `${p.ministryYears}년` : ''],
    ['목사 안수', p.ordinationYear ? `${p.ordinationYear}년 (${thisYear - p.ordinationYear + 1}년차)` : ''],
    ['현재', [p.currentRole, p.currentChurch, p.currentRegion].filter(Boolean).join(' · ')],
    ['희망 지역', [...p.hopeRegionTypes, ...p.hopeSidos].join(', ') || '지역 무관'],
    ['지방·농어촌', p.ruralCalling ? RURAL_LABEL[p.ruralCalling] : ''],
    ['희망 교회 규모', p.churchSizePrefs.join(', ') || '규모 무관'],
    ['부임 가능 시기', p.availableFrom],
    ['필요 조건', [p.needs.join(', '), p.needsNote].filter(Boolean).join(' / ')],
    ['가족', p.familyNote],
  ];
  const sermons = p.sermonUrls.filter(Boolean);

  return (
    <div className="pc-profile">
      <dl className="pc-profile__rows">
        {rows.filter(([, v]) => v).map(([k, v]) => (
          <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
        ))}
      </dl>

      <section className="pc-profile__sect">
        <h4>목회 철학</h4>
        {p.philosophyKeywords.length > 0 && (
          <div className="pc-card__tags">{p.philosophyKeywords.map((k) => <span key={k} className="pc-tag">#{k}</span>)}</div>
        )}
        <p className="pc-pre">{p.philosophy}</p>
      </section>

      {(p.strengthKeywords.length > 0 || p.strengths) && (
        <section className="pc-profile__sect">
          <h4>사역 강점</h4>
          {p.strengthKeywords.length > 0 && (
            <div className="pc-card__tags">{p.strengthKeywords.map((k) => <span key={k} className="pc-tag">#{k}</span>)}</div>
          )}
          {p.strengths && <p className="pc-pre">{p.strengths}</p>}
        </section>
      )}

      {sermons.length > 0 && (
        <section className="pc-profile__sect">
          <h4>설교 영상</h4>
          <div className="pc-sermons">
            {sermons.map((url) => (
              <a key={url} href={url} target="_blank" rel="noreferrer" className="pc-sermon">
                {youtubeThumb(url) ? <img src={youtubeThumb(url)} alt="설교 영상" /> : <span>▶</span>}
              </a>
            ))}
          </div>
        </section>
      )}

      {(p.education || p.career) && (
        <section className="pc-profile__sect">
          <h4>학력·경력</h4>
          {p.education && <p className="pc-pre">{p.education}</p>}
          {p.career && <p className="pc-pre">{p.career}</p>}
        </section>
      )}

      {onOpenFile && (p.files?.length ?? 0) > 0 && (
        <section className="pc-profile__sect">
          <h4>구직 서류</h4>
          <ul className="pc-files__list">
            {p.files!.map((f) => (
              <li key={f.id}>
                <span className="pc-files__kind">{f.kind}</span>
                <button type="button" className="pc-files__name" onClick={() => onOpenFile(f)}>{f.name}</button>
                <span className="pc-files__meta">{fileSizeText(f.size)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
